import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Navigate, Outlet, Link, useNavigate, useLocation } from 'react-router'
import classNames from 'classnames'
import {
  SignalIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  PauseCircleIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline'
import { StopCircleIcon, PlayIcon } from '@heroicons/react/24/solid'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { useStreamsVisibility, streamsSubtreeGuard } from 'hooks/useV1Visible'
import { useSocketToken } from 'context/socketToken'
import { useEventsList } from 'context/eventsList'
import { LoadingPage, LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { useSystemEvents, BotRevokedEnvelope } from 'hooks/useOverlaySubscription'
import { YouTubeSessionWidget } from 'components/YouTubeSessionWidget'
import { StreamKeyAlertBanner } from 'components/StreamKeyAlertBanner'
import { eventHasYouTubeSource } from 'components/EventYouTubeSources'
import { useEventYouTubeSessions } from 'hooks/useYouTubeSessions'
import { channelGuidanceFor } from 'lib/revocationCopy'
import { eventAccessFor } from 'lib/eventAccess'
import {
  graphqlRequest,
  formatLocalDateTime,
  withBotName,
  setStreamChannelLogins,
  clearStreamChannelLogins
} from 'lib/helpers'
import { ADMIN_PATH, EVENTS_PATH, eventPath, eventSectionPath } from 'lib/paths'
import {
  EventDetail,
  EventStatus,
  EventSummary,
  BotInfo,
  ChannelSubscription,
  DeletionResult
} from 'types/api'
import { EventDetailContextValue } from 'hooks/useEventDetail'
import getEventQuery from 'graphql/getEvent.graphql'
import startEventMutation from 'graphql/startEvent.graphql'
import stopEventMutation from 'graphql/stopEvent.graphql'
import endEventMutation from 'graphql/endEvent.graphql'
import unEndEventMutation from 'graphql/unEndEvent.graphql'
import updateEventNameMutation from 'graphql/updateEventName.graphql'
import updateEventDescriptionMutation from 'graphql/updateEventDescription.graphql'
import deleteEventMutation from 'graphql/deleteEvent.graphql'
import getBotInfoQuery from 'graphql/getBotInfo.graphql'
import listSubscriptionsQuery from 'graphql/listSubscriptions.graphql'

const STATUS_PILL: Record<EventStatus, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30',
  INACTIVE: 'bg-amber-500/15 text-amber-400 ring-amber-500/30',
  ENDED: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30'
}

const STATUS_LABEL: Record<EventStatus, string> = {
  ACTIVE: 'Live',
  INACTIVE: 'Not live',
  ENDED: 'Ended'
}

const YT_SESSIONS_SETTLE_MS = 1500

const eventSummaryOf = (e: EventDetail): EventSummary => ({
  eventId: e.eventId,
  slug: e.slug,
  name: e.name,
  description: e.description,
  status: e.status,
  createdAt: e.createdAt,
  startedAt: e.startedAt,
  endedAt: e.endedAt
})

export const GO_LIVE_YOUTUBE_NOTES: Readonly<Record<string, string>> = Object.freeze({
  HOST_ONLY_SKIPPED: 'The stream is live. The host\'s YouTube chat did not start: only the host can start it.',
  RUNNING_FOR_QUICK_OVERLAYS: 'Your YouTube chat is running for Quick Overlays only, so this stream does not show it or count its votes. Stop it, then start it for this stream in the YouTube card.',
  RUNNING_FOR_ANOTHER_STREAM: 'Your YouTube chat is running for another stream. Stop it there, then start it for this stream in the YouTube card.'
})

export const goLiveYouTubeNoteFor = (code: string | null | undefined): string | null =>
  code != null && Object.prototype.hasOwnProperty.call(GO_LIVE_YOUTUBE_NOTES, code)
    ? GO_LIVE_YOUTUBE_NOTES[code]
    : null

const RevocationBannerRow = ({
  banner,
  onDismiss
}: {
  banner: RevocationBanner
  onDismiss: () => void
}): JSX.Element => {
  const guidance = channelGuidanceFor(banner.reason)
  return (
    <div
      data-testid={`bot-revoked-banner-${banner.subscriptionId}`}
      className='flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200'
    >
      <ExclamationTriangleIcon className='size-5 shrink-0 text-red-300' aria-hidden='true' />
      <div className='flex-1'>
        <div className='font-medium text-red-100'>
          {guidance.title} — <span className='font-mono'>#{banner.channelLogin}</span>
        </div>
        <p className='mt-0.5 text-xs text-red-200/80'>
          {guidance.message} Votes from this channel have stopped.
        </p>
      </div>
      <Link
        to={ADMIN_PATH}
        className='shrink-0 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-100 hover:bg-red-500/20'
      >
        {guidance.actionLabel}
      </Link>
      <button
        onClick={onDismiss}
        title='Dismiss for this session'
        className='shrink-0 rounded-md p-1.5 text-red-200/70 hover:bg-red-500/10 hover:text-red-100'
      >
        <XMarkIcon className='size-4' aria-hidden='true' />
      </button>
    </div>
  )
}

export type LifecycleAction = 'goLive' | 'stop' | 'end' | 'unEnd'

const LifecycleButtons = ({
  status,
  showsLifecycleChrome,
  busyAction,
  onGoLive,
  onStop,
  onEnd,
  onUnEnd
}: {
  status: EventStatus
  showsLifecycleChrome: boolean
  busyAction: LifecycleAction | null
  onGoLive: () => void
  onStop: () => void
  onEnd: () => void
  onUnEnd: () => void
}): JSX.Element => {
  const busy = busyAction !== null
  const parked = (action: LifecycleAction): boolean => busy && busyAction !== action
  return (
    <div className='flex flex-wrap items-center gap-2'>
      {status === 'INACTIVE' && showsLifecycleChrome && (
        <button
          data-testid='go-live-button'
          onClick={onGoLive}
          disabled={busy}
          title='Start chat listening on the bound channels'
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-medium text-black hover:bg-emerald-400 disabled:cursor-not-allowed',
            parked('goLive') && 'opacity-40'
          )}
        >
          {busyAction === 'goLive' ? <LoadingSpinner /> : <PlayIcon className='size-3.5' aria-hidden='true' />}
          Go live
        </button>
      )}
      {status === 'ACTIVE' && (
        <button
          data-testid='stop-event-button'
          onClick={onStop}
          disabled={busy}
          title='Pause chat listening, data stays intact'
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-md border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-500/10 disabled:cursor-not-allowed',
            parked('stop') && 'opacity-40'
          )}
        >
          {busyAction === 'stop' ? <LoadingSpinner /> : <PauseCircleIcon className='size-3.5' aria-hidden='true' />}
          Stop
        </button>
      )}
      {(status === 'ACTIVE' || (status === 'INACTIVE' && showsLifecycleChrome)) && (
        <button
          data-testid='end-event-button'
          onClick={onEnd}
          disabled={busy}
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-md bg-red-500 px-3 py-1.5 text-xs font-medium text-black hover:bg-red-400 disabled:cursor-not-allowed',
            parked('end') && 'opacity-40'
          )}
        >
          {busyAction === 'end' ? <LoadingSpinner /> : <StopCircleIcon className='size-3.5' aria-hidden='true' />}
          End stream
        </button>
      )}
      {status === 'ENDED' && showsLifecycleChrome && (
        <button
          data-testid='un-end-event-button'
          onClick={onUnEnd}
          disabled={busy}
          title='Reopen this stream for editing, does not restart chat listening'
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-500/10 disabled:cursor-not-allowed',
            parked('unEnd') && 'opacity-40'
          )}
        >
          {busyAction === 'unEnd' ? <LoadingSpinner /> : <ArrowUturnLeftIcon className='size-3.5' aria-hidden='true' />}
          Un-end
        </button>
      )}
    </div>
  )
}

const EventHeader = ({
  event,
  showsLifecycleChrome,
  busyAction,
  canEnd,
  onGoLive,
  onStop,
  onEnd,
  onUnEnd,
  isOverview,
  canManage,
  deleteBusy,
  onSaveName,
  onSaveDescription,
  onDelete
}: {
  event: EventDetail
  showsLifecycleChrome: boolean
  busyAction: LifecycleAction | null
  canEnd: boolean
  onGoLive: () => void
  onStop: () => void
  onEnd: () => void
  onUnEnd: () => void
  isOverview: boolean
  canManage: boolean
  deleteBusy: boolean
  onSaveName: (name: string) => Promise<void>
  onSaveDescription: (description: string) => Promise<void>
  onDelete: () => void
}): JSX.Element => {
  const ended = event.status === 'ENDED'
  const metaShown = ended
    ? event.endedAt !== null
    : event.status === 'ACTIVE' && event.startedAt !== null
  const hasDescription = event.description !== null && event.description.length > 0

  const [editingDesc, setEditingDesc] = useState(false)
  const [descDraft, setDescDraft] = useState('')
  const [descSaving, setDescSaving] = useState(false)
  const [descError, setDescError] = useState<string | null>(null)

  const beginEditDesc = (): void => {
    setDescDraft(event.description ?? '')
    setDescError(null)
    setEditingDesc(true)
  }
  const saveDesc = async (): Promise<void> => {
    setDescSaving(true)
    setDescError(null)
    try {
      await onSaveDescription(descDraft)
      setEditingDesc(false)
    } catch (err) {
      setDescError(err instanceof Error ? err.message : 'Failed to save description')
    } finally {
      setDescSaving(false)
    }
  }

  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)

  const beginEditName = (): void => {
    setNameDraft(event.name)
    setNameError(null)
    setEditingName(true)
  }
  const saveName = async (): Promise<void> => {
    if (nameDraft.trim().length === 0) return
    setNameSaving(true)
    setNameError(null)
    try {
      await onSaveName(nameDraft)
      setEditingName(false)
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to save name')
    } finally {
      setNameSaving(false)
    }
  }

  if (!isOverview) {
    return (
      <header
        className='mx-4 mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-white/5 bg-dm-bg-secondary px-4 py-3'
        data-testid='event-header'
      >
        <div className='min-w-0 flex-1 basis-56'>
          <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1'>
            <h3 className='text-base font-semibold text-dm-text-primary'>{event.name}</h3>
            <span
              data-testid='event-status-pill'
              className={classNames(
                'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                STATUS_PILL[event.status]
              )}
            >
              {STATUS_LABEL[event.status]}
            </span>
            {ended
              ? (event.endedAt !== null && (
                <span className='inline-flex items-center gap-1 text-[11px] text-dm-text-accent'>
                  <GlobeAltIcon className='size-3' aria-hidden='true' />
                  Ended {formatLocalDateTime(event.endedAt)}
                </span>
                ))
              : (event.status === 'ACTIVE' && event.startedAt !== null && (
                <span className='inline-flex items-center gap-1 text-[11px] text-dm-text-accent'>
                  <SignalIcon className='size-3' aria-hidden='true' />
                  Live since {formatLocalDateTime(event.startedAt)}
                </span>
                ))}
            {metaShown && hasDescription && (
              <span aria-hidden='true' className='text-lg leading-none text-dm-text-accent/40'>·</span>
            )}
            {hasDescription && (
              <span className='text-[11px] text-dm-text-accent'>{event.description}</span>
            )}
          </div>
          {event.status === 'INACTIVE' && showsLifecycleChrome && (
            <p className='mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1 text-[11px] text-amber-300'>
              <InformationCircleIcon className='size-3.5' aria-hidden='true' />
              Not live: press go live to start listening on your bound channels.
            </p>
          )}
        </div>
        <div className='flex min-w-0 max-w-full flex-wrap items-center gap-2'>
          {canEnd && eventHasYouTubeSource(event) && (
            <YouTubeSessionWidget
              scope={{ kind: 'event', eventId: event.eventId }}
              variant='compact'
              ownSourceAttached={event.youtubeSourceAttached === true}
              isHost={event.isHost === true}
            />
          )}
          {canEnd && (
            <LifecycleButtons
              status={event.status}
              showsLifecycleChrome={showsLifecycleChrome}
              busyAction={busyAction}
              onGoLive={onGoLive}
              onStop={onStop}
              onEnd={onEnd}
              onUnEnd={onUnEnd}
            />
          )}
        </div>
      </header>
    )
  }

  return (
    <header
      className='mx-4 mt-4 rounded-xl border border-white/5 bg-dm-bg-secondary px-5 py-4'
      data-testid='event-header'
    >
      <div className='flex flex-wrap items-start justify-between gap-x-4 gap-y-3'>
        <div className='min-w-0 flex-1 basis-56'>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5'>
            {editingName
              ? (
                <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
                  <input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    autoFocus
                    placeholder='Stream name'
                    data-testid='event-name-input'
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setEditingName(false)
                      if (e.key === 'Enter') { e.preventDefault(); void saveName() }
                    }}
                    className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-1.5 text-xl font-bold text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
                  />
                  <button
                    type='button'
                    onClick={() => { void saveName() }}
                    disabled={nameSaving || nameDraft.trim().length === 0}
                    data-testid='event-name-save'
                    className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {nameSaving ? <LoadingSpinner /> : <CheckIcon className='size-3.5' aria-hidden='true' />}
                    Save
                  </button>
                  <button
                    type='button'
                    onClick={() => setEditingName(false)}
                    disabled={nameSaving}
                    className='rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                  >
                    Cancel
                  </button>
                </div>
                )
              : (
                <h1
                  onDoubleClick={beginEditName}
                  title='Double-click to edit'
                  data-testid='event-name'
                  className='group flex cursor-text items-center gap-2 text-2xl font-bold leading-tight text-dm-text-primary'
                >
                  {event.name}
                  <PencilSquareIcon
                    className='size-4 shrink-0 text-dm-text-accent opacity-0 transition-opacity group-hover:opacity-100'
                    aria-hidden='true'
                  />
                </h1>
                )}
            <span
              data-testid='event-status-pill'
              className={classNames(
                'inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset',
                STATUS_PILL[event.status]
              )}
            >
              {STATUS_LABEL[event.status]}
            </span>
          </div>
          {nameError !== null && (
            <p className='mt-1 text-[11px] text-red-400' data-testid='event-name-error'>{nameError}</p>
          )}
          <div className='mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-dm-text-accent'>
            <span className='inline-flex items-center gap-1'>
              <CalendarDaysIcon className='size-3' aria-hidden='true' />
              Created {formatLocalDateTime(event.createdAt)}
            </span>
            {ended
              ? (event.endedAt !== null && (
                <span className='inline-flex items-center gap-1'>
                  <GlobeAltIcon className='size-3' aria-hidden='true' />
                  Ended {formatLocalDateTime(event.endedAt)}
                </span>
                ))
              : (event.status === 'ACTIVE' && event.startedAt !== null && (
                <span className='inline-flex items-center gap-1'>
                  <SignalIcon className='size-3' aria-hidden='true' />
                  Live since {formatLocalDateTime(event.startedAt)}
                </span>
                ))}
          </div>
        </div>
        <div className='flex min-w-0 max-w-full flex-wrap items-center gap-2'>
          {canEnd && eventHasYouTubeSource(event) && (
            <YouTubeSessionWidget
              scope={{ kind: 'event', eventId: event.eventId }}
              variant='compact'
              ownSourceAttached={event.youtubeSourceAttached === true}
              isHost={event.isHost === true}
            />
          )}
          {canEnd && (
            <LifecycleButtons
              status={event.status}
              showsLifecycleChrome={showsLifecycleChrome}
              busyAction={busyAction}
              onGoLive={onGoLive}
              onStop={onStop}
              onEnd={onEnd}
              onUnEnd={onUnEnd}
            />
          )}
          {canManage && (
            <button
              data-testid='delete-event-button'
              onClick={onDelete}
              disabled={deleteBusy || event.status === 'ACTIVE'}
              title={event.status === 'ACTIVE'
                ? 'End the stream first'
                : 'Permanently delete this stream and everything in it'}
              className='inline-flex items-center gap-1.5 rounded-md border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-50'
            >
              {deleteBusy ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
              Delete stream
            </button>
          )}
        </div>
      </div>

      <div className='mt-3 border-t border-white/5 pt-3'>
        {editingDesc
          ? (
            <div>
              <textarea
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
                autoFocus
                rows={2}
                placeholder='Describe this stream…'
                onKeyDown={(e) => { if (e.key === 'Escape') setEditingDesc(false) }}
                className='w-full resize-y rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
              />
              {descError !== null && <p className='mt-1 text-[11px] text-red-400'>{descError}</p>}
              <div className='mt-2 flex items-center gap-2'>
                <button
                  type='button'
                  onClick={() => { void saveDesc() }}
                  disabled={descSaving}
                  className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
                >
                  {descSaving ? <LoadingSpinner /> : <CheckIcon className='size-3.5' aria-hidden='true' />}
                  Save
                </button>
                <button
                  type='button'
                  onClick={() => setEditingDesc(false)}
                  disabled={descSaving}
                  className='rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
                >
                  Cancel
                </button>
              </div>
            </div>
            )
          : (
            <div
              onDoubleClick={beginEditDesc}
              title='Double-click to edit'
              data-testid='event-description'
              className='group flex cursor-text items-start gap-2'
            >
              {hasDescription
                ? <p className='min-w-0 flex-1 whitespace-pre-wrap text-sm text-dm-text-secondary'>{event.description}</p>
                : <p className='min-w-0 flex-1 text-sm italic text-dm-text-accent'>No description. Double-click to add one.</p>}
              <PencilSquareIcon
                className='mt-0.5 size-3.5 shrink-0 text-dm-text-accent opacity-0 transition-opacity group-hover:opacity-100'
                aria-hidden='true'
              />
            </div>
            )}
      </div>

      {event.status === 'INACTIVE' && showsLifecycleChrome && (
        <p className='mt-3 inline-flex items-center gap-1.5 rounded-md border border-amber-500/20 bg-amber-500/5 px-2.5 py-1 text-[11px] text-amber-300'>
          <InformationCircleIcon className='size-3.5' aria-hidden='true' />
          Not live: press go live to start listening on your bound channels.
        </p>
      )}
    </header>
  )
}

interface RevocationBanner {
  subscriptionId: string
  channelLogin: string
  botUserId: string
  reason: string
  revokedAt: number
}

export const EventDetailLayout = (): JSX.Element | null => {
  const { auth0Token, streamToolsUser } = useAuth()
  const { superuser, status, featureFor } = useEntitlements()
  const streamsGuard = streamsSubtreeGuard(useStreamsVisibility())
  const { socketToken } = useSocketToken()
  const {
    events: allEvents,
    patchEvent,
    removeEvent,
    prependEvent,
    loading: listLoading,
    error: listError,
    setCurrentStream,
    clearCurrentStream
  } = useEventsList()
  const { eventId = '' } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const ytEventStore = useEventYouTubeSessions(eventId, false)
  const ytSettleTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null)
  const shownEventId = useRef<string | null>(null)

  const [event, setEvent] = useState<EventDetail | null>(null)
  const [bot, setBot] = useState<BotInfo | null>(null)
  const [subscriptions, setSubscriptions] = useState<ChannelSubscription[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [statusBusyAction, setStatusBusyAction] = useState<LifecycleAction | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [goLiveNote, setGoLiveNote] = useState<string | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [revocationBanners, setRevocationBanners] = useState<RevocationBanner[]>([])

  const refetchEvent = useCallback(async () => {
    if (auth0Token === undefined || eventId === '') return
    const { event: detail } = await graphqlRequest<{ event: EventDetail | null }>(
      getEventQuery, { eventId }, auth0Token
    )
    if (detail === null) {
      setNotFound(true)
      setEvent(null)
    } else {
      setEvent(detail)
    }
  }, [auth0Token, eventId])

  const refetchSubscriptions = useCallback(async () => {
    if (auth0Token === undefined || eventId === '') return
    const { subscriptions: subsResp } = await graphqlRequest<{ subscriptions: ChannelSubscription[] }>(
      listSubscriptionsQuery, { eventId }, auth0Token
    )
    setSubscriptions(subsResp ?? [])
  }, [auth0Token, eventId])

  const fetchAll = useCallback(async () => {
    if (auth0Token === undefined || eventId === '') return
    setLoading(true)
    setFetchError(null)
    setNotFound(false)
    try {
      const [
        { event: detail },
        { bot: botResp },
        { subscriptions: subsResp }
      ] = await Promise.all([
        graphqlRequest<{ event: EventDetail | null }>(getEventQuery, { eventId }, auth0Token),
        graphqlRequest<{ bot: BotInfo | null }>(getBotInfoQuery, {}, auth0Token),
        graphqlRequest<{ subscriptions: ChannelSubscription[] }>(listSubscriptionsQuery, { eventId }, auth0Token)
      ])
      setBot(botResp)
      setSubscriptions(subsResp ?? [])
      if (detail === null) {
        setNotFound(true)
        setEvent(null)
      } else {
        setEvent(detail)
      }
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load stream')
    } finally {
      setLoading(false)
    }
  }, [auth0Token, eventId])

  useEffect(() => { void fetchAll() }, [fetchAll])

  const viewerIsMember =
    event?.members.some((m) => m.userId === streamToolsUser?.userId) === true
  useEffect(() => {
    if (event === null || event.eventId !== eventId) return
    setCurrentStream({ event: eventSummaryOf(event), viewerIsMember })
  }, [event, eventId, viewerIsMember, setCurrentStream])
  useEffect(() => () => { clearCurrentStream(eventId) }, [eventId, clearCurrentStream])

  const heldEventId = event?.eventId ?? null
  useEffect(() => {
    if (event === null || event.eventId !== eventId || !viewerIsMember) return
    if (listLoading || listError !== null) return
    if (allEvents.some((e) => e.eventId === event.eventId)) return
    prependEvent(eventSummaryOf(event))
  }, [heldEventId, eventId, viewerIsMember, listLoading, listError, prependEvent])

  const boundChannels = event?.channels
  useEffect(() => {
    setStreamChannelLogins(boundChannels ?? [])
    return () => { clearStreamChannelLogins() }
  }, [boundChannels])

  useEffect(() => {
    shownEventId.current = eventId
    setGoLiveNote(null)
    return () => {
      shownEventId.current = null
      if (ytSettleTimer.current !== null) {
        globalThis.clearTimeout(ytSettleTimer.current)
        ytSettleTimer.current = null
      }
    }
  }, [eventId])

  const onBotRevoked = useCallback((envelope: BotRevokedEnvelope) => {
    setRevocationBanners((prev) => {
      const filtered = prev.filter((b) => b.subscriptionId !== envelope.subscriptionId)
      return [...filtered, {
        subscriptionId: envelope.subscriptionId,
        channelLogin: envelope.channelLogin,
        botUserId: envelope.botUserId,
        reason: envelope.reason,
        revokedAt: envelope.revokedAt
      }]
    })
  }, [])

  useSystemEvents(
    event !== null ? `${event.slug}:system:admin` : null,
    socketToken ?? null,
    { onBotRevoked }
  )

  const dismissBanner = (subscriptionId: string): void => {
    setRevocationBanners((prev) => prev.filter((b) => b.subscriptionId !== subscriptionId))
  }

  const rereadStreamSessionsSoon = (): void => {
    if (event === null || event.eventId !== shownEventId.current) return
    if (!eventHasYouTubeSource(event)) return
    if (ytSettleTimer.current !== null) globalThis.clearTimeout(ytSettleTimer.current)
    const store = ytEventStore
    ytSettleTimer.current = globalThis.setTimeout(() => {
      ytSettleTimer.current = null
      void store.refresh()
    }, YT_SESSIONS_SETTLE_MS)
  }

  const handleGoLive = async (): Promise<void> => {
    if (auth0Token === undefined || event === null || statusBusyAction !== null) return
    setStatusBusyAction('goLive')
    setStatusError(null)
    setGoLiveNote(null)
    try {
      const { event: updated } = await graphqlRequest<{ event: { eventId: string, status: EventStatus, startedAt: string | null, youtubeAutoStart?: string | null } }>(
        startEventMutation, { eventId }, auth0Token
      )
      setEvent({ ...event, status: updated.status, startedAt: updated.startedAt })
      setGoLiveNote(goLiveYouTubeNoteFor(updated.youtubeAutoStart))
      patchEvent(eventId, { status: updated.status, startedAt: updated.startedAt })
      rereadStreamSessionsSoon()
      void refetchSubscriptions()
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to go live'
      setStatusError(withBotName(raw, bot !== null ? bot.login : null))
    } finally {
      setStatusBusyAction(null)
    }
  }

  const foldStreamSessionsStopped = (): void => {
    const stoppedAt = new Date().toISOString()
    for (const row of ytEventStore.sessions) {
      if (row.status === 'RUNNING') ytEventStore.upsert({ ...row, status: 'STOPPED', stopReason: 'EVENT_STOPPED', stoppedAt })
    }
  }

  const handleStop = async (): Promise<void> => {
    if (auth0Token === undefined || event === null || statusBusyAction !== null) return
    setStatusBusyAction('stop')
    setStatusError(null)
    try {
      const { event: updated } = await graphqlRequest<{ event: { eventId: string, status: EventStatus } }>(
        stopEventMutation, { eventId }, auth0Token
      )
      setEvent({
        ...event,
        status: updated.status,
        polls: event.polls.map((p) => p.status === 'ACTIVE' ? { ...p, status: 'INACTIVE' } : p),
        trivias: event.trivias.map((t) => t.status === 'ACTIVE' ? { ...t, status: 'INACTIVE' } : t),
        tugs: event.tugs.map((t) => t.status === 'ACTIVE' ? { ...t, status: 'INACTIVE' } : t)
      })
      foldStreamSessionsStopped()
      rereadStreamSessionsSoon()
      patchEvent(eventId, { status: updated.status })
      void refetchSubscriptions()
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to stop stream')
    } finally {
      setStatusBusyAction(null)
    }
  }

  const handleUnEnd = async (): Promise<void> => {
    if (auth0Token === undefined || event === null || statusBusyAction !== null) return
    const ok = globalThis.confirm(
      `Un-end "${event.name}"?\n\nThis reopens the stream for editing. It does NOT restart chat listening — the stream comes back as "Not live", its channel bindings were removed when it ended, and you must re-add channels on Integrations and press Go live to listen again.\n\nWrap-up stats will be recomputed the next time you end the stream.\n\nReopen this stream?`
    )
    if (!ok) return
    setStatusBusyAction('unEnd')
    setStatusError(null)
    try {
      const { event: updated } = await graphqlRequest<{ event: { eventId: string, status: EventStatus, endedAt: string | null } }>(
        unEndEventMutation, { eventId }, auth0Token
      )
      setEvent({ ...event, status: updated.status, endedAt: updated.endedAt })
      patchEvent(eventId, { status: updated.status, endedAt: updated.endedAt })
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to un-end stream')
    } finally {
      setStatusBusyAction(null)
    }
  }

  const handleEnd = async (): Promise<void> => {
    if (auth0Token === undefined || event === null || statusBusyAction !== null) return
    const ok = globalThis.confirm(
      'End this stream? Any LIVE polls or tug-of-wars will be stopped, channel bindings will be removed, and Twitch subscriptions for channels not used elsewhere will be torn down.'
    )
    if (!ok) return
    setStatusBusyAction('end')
    setStatusError(null)
    try {
      const { event: updated } = await graphqlRequest<{ event: { eventId: string, status: EventStatus, endedAt: string | null } }>(
        endEventMutation, { eventId }, auth0Token
      )
      setEvent({
        ...event,
        status: updated.status,
        endedAt: updated.endedAt,
        channels: [],
        youtubeForeignSources: [],
        polls: event.polls.map((p) => p.status === 'ACTIVE' ? { ...p, status: 'INACTIVE' } : p),
        trivias: event.trivias.map((t) => t.status === 'ACTIVE' ? { ...t, status: 'INACTIVE' } : t),
        tugs: event.tugs.map((t) => t.status === 'ACTIVE' ? { ...t, status: 'INACTIVE' } : t)
      })
      foldStreamSessionsStopped()
      patchEvent(eventId, { status: updated.status, endedAt: updated.endedAt })
      void refetchSubscriptions()
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to end stream')
    } finally {
      setStatusBusyAction(null)
    }
  }

  const handleSaveDescription = async (description: string): Promise<void> => {
    if (auth0Token === undefined || event === null) return
    const trimmed = description.trim()
    const { event: updated } = await graphqlRequest<{ event: { eventId: string, description: string | null } }>(
      updateEventDescriptionMutation,
      { eventId, description: trimmed.length > 0 ? trimmed : null },
      auth0Token
    )
    setEvent({ ...event, description: updated.description ?? null })
  }

  const handleSaveName = async (name: string): Promise<void> => {
    if (auth0Token === undefined || event === null) return
    const trimmed = name.trim()
    const { event: updated } = await graphqlRequest<{ event: { eventId: string, name: string } }>(
      updateEventNameMutation,
      { eventId, name: trimmed },
      auth0Token
    )
    setEvent({ ...event, name: updated.name })
    patchEvent(eventId, { name: updated.name })
  }

  const handleDelete = async (): Promise<void> => {
    if (auth0Token === undefined || event === null || deleteBusy) return
    const ok = globalThis.confirm(
      `Delete "${event.name}"?\n\nThis permanently deletes the stream AND all of its polls, tug-of-wars, scoreboards, chat overlays, emote walls and vote history. It cannot be undone.`
    )
    if (!ok) return
    setDeleteBusy(true)
    setStatusError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(
        deleteEventMutation, { eventId }, auth0Token
      )
      if (result.deleted) {
        removeEvent(eventId)
        void navigate(EVENTS_PATH)
      } else {
        setStatusError('Could not delete this stream.')
      }
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : 'Failed to delete stream')
    } finally {
      setDeleteBusy(false)
    }
  }

  if (streamToolsUser === null) return <div>Unauthorized</div>
  if (auth0Token === undefined) return <LoadingPage />

  if (streamsGuard === 'wait') {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <LoadingSpinner />
      </div>
    )
  }
  if (streamsGuard === 'redirect') return <Navigate to={EVENTS_PATH} replace />

  if (notFound) return <Navigate to={EVENTS_PATH} replace />

  if (loading && event === null) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <LoadingSpinner />
      </div>
    )
  }

  if (fetchError !== null && event === null) {
    return (
      <div className='flex flex-1 items-center justify-center px-8 py-16'>
        <div className='flex max-w-md flex-col items-center gap-3 text-center text-sm'>
          <ExclamationTriangleIcon className='size-8 text-red-400' aria-hidden='true' />
          <p className='text-red-300'>{fetchError}</p>
          <button
            onClick={() => { void fetchAll() }}
            className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (event === null) return null

  const { canManage: canEndEvent } = eventAccessFor({
    members: event.members,
    userId: streamToolsUser?.userId,
    superuser
  })
  const isOverview = location.pathname === eventPath(eventId) ||
    location.pathname === eventSectionPath(eventId, 'overview')
  const showsLifecycleChrome = status !== 'ready' || featureFor('interactiveContent')

  const ctx: EventDetailContextValue = {
    event,
    bot,
    subscriptions,
    allEvents,
    setEvent,
    refetchEvent,
    refetchSubscriptions
  }

  return (
    <main
      data-testid='event-detail-page'
      className='flex flex-1 flex-col'
    >
      <div className='flex flex-1 flex-col'>
        <EventHeader
          event={event}
          showsLifecycleChrome={showsLifecycleChrome}
          busyAction={statusBusyAction}
          canEnd={canEndEvent}
          onGoLive={() => { void handleGoLive() }}
          onStop={() => { void handleStop() }}
          onEnd={() => { void handleEnd() }}
          onUnEnd={() => { void handleUnEnd() }}
          isOverview={isOverview}
          canManage={canEndEvent}
          deleteBusy={deleteBusy}
          onSaveName={handleSaveName}
          onSaveDescription={handleSaveDescription}
          onDelete={() => { void handleDelete() }}
        />
        {revocationBanners.length > 0 && (
          <div className='mx-8 mt-3 flex flex-col gap-2'>
            {revocationBanners.map((banner) => (
              <RevocationBannerRow
                key={banner.subscriptionId}
                banner={banner}
                onDismiss={() => dismissBanner(banner.subscriptionId)}
              />
            ))}
          </div>
        )}
        {statusError !== null && (
          <div className='mx-8 mt-3'>
            <ErrorInline onDismiss={() => setStatusError(null)}>{statusError}</ErrorInline>
          </div>
        )}
        {goLiveNote != null && (
          <div
            role='status'
            data-testid='go-live-youtube-note'
            className='mx-4 mt-3 flex items-start gap-2 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-xs text-dm-text-secondary sm:mx-8'
          >
            <InformationCircleIcon className='mt-0.5 size-4 shrink-0 text-brand' aria-hidden='true' />
            <span className='min-w-0 flex-1'>{goLiveNote}</span>
            <button
              type='button'
              onClick={() => setGoLiveNote(null)}
              title='Dismiss'
              className='shrink-0 rounded p-0.5 text-dm-text-accent hover:text-dm-text-primary'
            >
              <XMarkIcon className='size-4' aria-hidden='true' />
              <span className='sr-only'>Dismiss</span>
            </button>
          </div>
        )}

        <StreamKeyAlertBanner
          eventId={event.eventId}
          streamKeyAlertAt={event.streamKeyAlertAt}
          canManage={canEndEvent}
          auth0Token={auth0Token}
          refetchEvent={refetchEvent}
        />
        <Outlet context={ctx} />
      </div>
    </main>
  )
}
